import { styled } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import csiCard, { CardSpecs } from "csi/card/_base";
import { boxShadows, grey } from "designSystem/styles/color";
import { useCache } from "designSystem/hooks";
import { CsiImageProps } from "designSystem/components/image/CsiImage";
import { Box, CsiImage, DataGridCardProps, GridColumn, OverflowText, Text } from "designSystem/index";
import { HOVER } from "designSystem/theme";
import locale from "csi/locale";
import { THUMBNAIL_IMAGE_SIZE } from "csi/model/image";
import DojoGalleryAttributes from "csi/page/DojoGalleryAttributes";
import { C8Node } from "csi/typings";
import React, { FunctionComponent } from "react";

const PREFIX = "AttributeCard";

const classes = {
	cardSelect: `${PREFIX}cardSelect`,
	fullHeight: `${PREFIX}fullHeight`,
	skeletonMargin: `${PREFIX}skeletonMargin`,
};

const StyledGridColumn = styled(GridColumn)(({
	theme: { spacing },
}) => ({
	[`& .${classes.cardSelect}`]: {
		[HOVER]: {
			boxShadow: boxShadows.card,
			cursor: "pointer",
		},
	},

	[`& .${classes.fullHeight}`]: {
		height: "100%",
	},

	[`& .${classes.skeletonMargin}`]: {
		marginBottom: spacing(3),
		marginLeft: spacing(3),
		marginRight: spacing(3),
	},
}));

export interface AttributeCardSkeletonProps {
	/** card specs */
	cardSpecs: CardSpecs;
}

export const AttributeCardSkeleton: FunctionComponent<AttributeCardSkeletonProps> = (props) => {
	const { cardSpecs: { attributeSpecs, imageSpec } } = props;

	const otherSpecs = csiCard.getAttributeSpecsMinusName(attributeSpecs);

	return (
		<StyledGridColumn spacing={3} standardBorder>
			{imageSpec &&
				<Skeleton height={THUMBNAIL_IMAGE_SIZE} variant="rectangular" />
			}
			<Box className={classes.skeletonMargin}>
				<Skeleton height={16} />
				{!!otherSpecs.length &&
					otherSpecs.map((spec, i) => (
						<Skeleton height={14} key={i} />
					))
				}
			</Box>
		</StyledGridColumn>
	);
};

export interface AttributeCardProps extends DataGridCardProps<C8Node> {
	/** card specs */
	cardSpecs: CardSpecs;

	/** additional properties to apply to card image */
	imageProps?: Partial<CsiImageProps>;

	/** callback to call when card is selected */
	onSelect?: VoidFunction;
}

export const AttributeCard: FunctionComponent<AttributeCardProps> = (props) => {
	const { cardSpecs, imageProps, item, onSelect } = props;
	const { attributeSpecs, imageSpec } = cardSpecs;
	const otherSpecs = csiCard.getAttributeSpecsMinusName(attributeSpecs);
	const { cardSelect, fullHeight } = classes;

	const cache = useCache();

	return (
		<GridColumn
			className={`${fullHeight} ${onSelect ? cardSelect : ""}`}
			growChild={1}
			onClick={onSelect}
			standardBorder
		>
			{imageSpec && (
				<CsiImage
					borderRadius="3px 3px 0 0"
					height={THUMBNAIL_IMAGE_SIZE}
					noImageComponent={(
						<Text
							lineHeight={16}
							res="NoImageAvailable"
							semiBold
							size={14}
							style={{ color: grey[800] }}
						/>
					)}
					node={item}
					path={imageSpec.heading.m_path}
					{...imageProps}
				/>
			)}
			<GridColumn spacing={1} spacingAround={3}>
				<OverflowText text={locale.getNodeConfiguredName(item, cache)} variant="h6" />
				{!!otherSpecs.length &&
					<DojoGalleryAttributes node={item} specs={otherSpecs} />
				}
			</GridColumn>
		</GridColumn>
	);
};

export default AttributeCard;
